import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CookieService } from 'ngx-cookie-service';
import { Subscription } from 'rxjs';
import { SseService } from 'src/app/services/SseService';
import { TemaService } from 'src/app/services/tema.services';

export interface ChatMensajeUI {
    role: 'user' | 'assistant';
    text: string;
    sugerencias?: any[];
    // Flujo en dos pasos: el texto libre sale primero y las condiciones traducidas llegan después
    traduciendo?: boolean;
    errorTraduccion?: boolean;
    noSoportadas?: TraduccionItemUI[];
    // Ya traducido, el texto libre repetiría las mismas condiciones: se pliega y se puede volver a ver
    textoPlegado?: boolean;
    verTexto?: boolean;
    // Chips por categoría del texto ("Tendencia", "Momentum"…); sin varias categorías se pintan en una sola fila
    grupos?: GrupoChipsUI[];
}

export interface GrupoChipsUI {
    titulo: string | null;
    sugerencias: any[];
}

export type DireccionUI = 'LONG' | 'SHORT';

export interface TraduccionItemUI {
    original: string;
    dsl: string;
    valido: boolean;
    motivo: string;
    direccion?: DireccionUI | null;
    grupo?: string | null;
}

// Lo que el modelo no puede adivinar de dónde se va a usar la condición
export interface ContextoAsistenteUI {
    timeframe?: string;
    mercado?: string;
    metaapi?: boolean;
    // Evita enviar al modelo catálogos que la consulta no necesita
    tipo?: 'tecnico' | 'ratio' | 'patron';
}

const ES_PETICION = /^[¿¡\s]*(dame|genera\w*|quiero|busca\w*|necesito|mu[eé]stra\w*|utiliza|usa|act[uú]a|explica\w*|qu[eé]|c[oó]mo|cu[aá]l\w*|por\s+qu[eé]|ahora|m[aá]s|otras?|haz|hazme|crea\w*|sugiere\w*|prop[oó]n\w*)(?=[\s,:?.]|$)/i;
const TIENE_OPERADOR = /[<>=]|\b(cruza\w*|cruce\w*|crosses|crossover|crossunder|cross)\b|por\s+(?:encima|debajo)\s+de|superior\s+a|inferior\s+a|mayor\s+que|menor\s+que|esta_cruzandose|se_estan_cruzando|esta_subiendo|esta_cayendo/i;

// Lo que ya trae condiciones (pegado de otra IA, o escrito a mano) solo hay que traducirlo: sin esto
// iría al paso de ideas, que generaría condiciones nuevas y gastaría una llamada de más.
export function pareceCondiciones(texto: string): boolean {
    return TIENE_OPERADOR.test(texto) && !ES_PETICION.test(texto);
}

export interface ChatIaDialogData {
    condicionActual: string;
    token: string;
    onCondicionChange?: (condicion: string, direccion?: DireccionUI) => void;
    initialQuery?: string;
    source?: string;
    // true: ideas en texto libre (como chatgpt.com) y luego traducción al DSL; false: flujo corto de siempre
    modoIdeas?: boolean;
    contexto?: ContextoAsistenteUI;
}

@Component({
    selector: 'app-chat-ia-dialog',
    templateUrl: './chat-ia-dialog.component.html',
    styleUrls: ['./chat-ia-dialog.component.scss']
})
export class ChatIaDialogComponent implements OnInit, OnDestroy {

    mensajes: ChatMensajeUI[] = [];
    inputText: string = '';
    loading: boolean = false;
    condicionSeleccionada: string = '';
    condicionaguardar: string = '';
    token: string = '';
    isDark: boolean = false;
    isMinimized: boolean = false;
    // Condiciones marcadas para guardar en bloque (solo en modo guardar, con onSaveCallback)
    condicionesSeleccionadas: string[] = [];
    listaAbierta: boolean = false;
    // Con condiciones marcadas la barra de selección sustituye al input; true = volver a escribir
    escribiendo: boolean = false;
    public onSaveCallback: ((condiciones: string[], direcciones: DireccionUI[]) => void) | null = null;
    // LONG/SHORT de cada condición traducida: el DSL no lo expresa y lo aplica la pantalla que la usa
    private direcciones = new Map<string, DireccionUI>();
    // Públicos para que la pantalla que reutiliza el diálogo abierto pueda cambiarlos
    public modoIdeas: boolean = false;
    public contexto: ContextoAsistenteUI = {};
    // Memoria del flujo de ideas: solo mientras el diálogo está abierto, nada se guarda en el servidor
    private historialIdeas: { role: 'user' | 'assistant'; content: string }[] = [];
    // Petición en curso: al reiniciar se cancela para que su respuesta no aparezca en el chat nuevo
    private peticion?: Subscription;
    private subtema: Subscription;

    constructor(
        public dialogRef: MatDialogRef<ChatIaDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: ChatIaDialogData,
        private _sseservice: SseService,
        private _cookieService: CookieService,
        private temaservicio: TemaService
    ) {}

    ngOnInit(): void {
        this.token = this.data.token || this._cookieService.get('auth_token');
        this.condicionSeleccionada = this.data.condicionActual || '';
        this.modoIdeas = !!this.data.modoIdeas;
        this.contexto = this.data.contexto || {};
        this.subtema = this.temaservicio.valDarkTheme.subscribe({
            next: (data: any) => { this.isDark = data; }
        });
        this.mostrarBienvenida();
        // El historial vive en el backend y sobrevive entre aperturas, pero la UI arranca vacía:
        // se borra al abrir para que el modelo no responda con memoria que el usuario no ve.
        // La consulta inicial espera al borrado para que no llegue antes y use el historial viejo.
        const enviarInicial = () => {
            // Si hay consulta inicial (p. ej. desde searchIndicatorInChatGPT), enviarla automáticamente
            if (this.data.initialQuery) {
                this.enviar(this.data.initialQuery);
            }
        };
        if (!this.token) {
            enviarInicial();
            return;
        }
        this.peticion = this._sseservice.apiDelete(this.urlBorrarChat, this.token).subscribe({
            next: () => enviarInicial(),
            error: () => enviarInicial()
        });
    }

    private get source(): string {
        return this.data.source || (this.data.onCondicionChange ? 'models' : 'algoritmobatch');
    }

    // Sin contexto el backend borra el historial de todas las pantallas del usuario
    private get urlBorrarChat(): string {
        return `conditions/chat?contexto=${encodeURIComponent(this.source)}`;
    }

    private mostrarBienvenida(): void {
        this.mensajes.push({
            role: 'assistant',
            text: 'Hola, soy tu asistente para construir estrategias de inversión & trading. Puedes pedirme estrategias con indicadores tecnicos ("ej. RSI > 50 "), datos fundamentales ("ej. Roe > 0.15), patrones ("ej. estrella fugaz"), o escribir una estrategia directamente y la traducire. Tambien puedo reconcer estrategias de otras aplicaciones (sqx, tradingview,etc))',
            sugerencias: [
                { tipo: 'condicion', etiqueta: 'Tecnicos', query: 'dame condiciones tecnicas', accion: 'refinar' },
                { tipo: 'condicion', etiqueta: 'Fundamentales', query: 'dame condiciones fundamentales', accion: 'refinar' },
                { tipo: 'condicion', etiqueta: 'Patrones', query: 'dame patrones', accion: 'refinar' }
            ]
        });
    }
    enviar(textoOverride?: string): void {
        const texto = (textoOverride ?? this.inputText).trim();
        if (!texto || this.loading) return;

        this.mensajes.push({ role: 'user', text: texto });
        this.inputText = '';
        this.loading = true;

        if (pareceCondiciones(texto)) {
            this.enviarPegado(texto);
        } else if (this.modoIdeas) {
            this.enviarIdeas(texto);
        } else {
            this.enviarCorto(texto);
        }
    }

    // Flujo de siempre: una sola llamada que traduce o busca y devuelve JSON con sugerencias
    private enviarCorto(texto: string): void {
        this.peticion = this._sseservice.apiPostNew(
            'conditions/interpretar_conditions',
            JSON.stringify({ input: texto, source: this.source }),
            this.token
        ).subscribe({
            next: (response: any) => {
                this.loading = false;
                const res = response?.response;
                if (!res) return;

                let textoIA = res.descripcion || '';
                if (res.modo === 'traduccion' && res.output) {
                    textoIA = `DSL: \`${res.output}\`\n${res.descripcion || ''}`;
                }

                this.mensajes.push({
                    role: 'assistant',
                    text: textoIA,
                    sugerencias: res.sugerencias || []
                });

                // Si es traducción directamente aplicable, actualizar condición preseleccionada
                if (res.modo === 'traduccion' && res.valido && res.output) {
                    this.condicionSeleccionada = res.output;
                    // Insertar chip para aplicar el DSL principal directamente
                    const lastMsg = this.mensajes[this.mensajes.length - 1];
                    if (lastMsg && lastMsg.role === 'assistant') {
                        lastMsg.sugerencias = [
                            { tipo: 'condicion', etiqueta: 'Usar esta condición', query: res.output, accion: 'aplicar' },
                            ...(res.sugerencias || [])
                        ];
                    }
                }

                this.scrollToBottom();
            },
            error: () => {
                this.loading = false;
                this.mensajes.push({ role: 'assistant', text: 'Error al procesar la consulta. Intenta de nuevo.' });
            }
        });
    }

    // Paso 1: ideas en texto libre, como chatgpt.com. Si falla (sin licencia, error del modelo) se cae al flujo corto.
    private enviarIdeas(texto: string): void {
        const contexto = { ...this.contexto, condicionActual: this.condicionSeleccionada || undefined };
        this.peticion = this._sseservice.apiPostNew(
            'conditions/ideas',
            JSON.stringify({ input: texto, historial: this.historialIdeas, contexto }),
            this.token
        ).subscribe({
            next: (response: any) => {
                const ideas: string = response?.texto || '';
                if (!ideas.trim()) {
                    this.enviarCorto(texto);
                    return;
                }
                this.historialIdeas.push({ role: 'user', content: texto }, { role: 'assistant', content: ideas });
                // Las vallas ``` no se pintan en un <pre>: se quitan y el texto queda tal cual
                const mensaje: ChatMensajeUI = {
                    role: 'assistant',
                    text: ideas.replace(/^\s*```.*\r?\n?/gm, '').trim(),
                    traduciendo: true
                };
                this.mensajes.push(mensaje);
                this.scrollToBottom();
                this.traducirIdeas(mensaje, ideas, contexto, false);
            },
            error: () => this.enviarCorto(texto)
        });
    }

    // Condiciones que el usuario trae de otra IA: solo se traducen, sin pasar por el paso de ideas.
    // El tipo del botón no aplica a lo pegado: el servidor decide los catálogos mirando el texto.
    private enviarPegado(texto: string): void {
        const mensaje: ChatMensajeUI = { role: 'assistant', text: 'Traducción de lo que escribiste:', traduciendo: true };
        this.mensajes.push(mensaje);
        this.scrollToBottom();
        this.traducirIdeas(mensaje, texto, { ...this.contexto, tipo: undefined }, true);
    }

    // Paso 2: el texto se pasa al DSL y el motor valida cada condición
    private traducirIdeas(mensaje: ChatMensajeUI, texto: string, contexto: any, pegado: boolean): void {
        this.peticion = this._sseservice.apiPostNew(
            'conditions/traducir_lote',
            JSON.stringify({ texto, contexto, pegado }),
            this.token
        ).subscribe({
            next: (response: any) => {
                const items: TraduccionItemUI[] = response?.items || [];
                const ejecutables = items.filter(i => i.valido && i.dsl);
                ejecutables.forEach(i => {
                    if (i.direccion) this.direcciones.set(i.dsl, i.direccion);
                    else this.direcciones.delete(i.dsl);
                });
                mensaje.sugerencias = ejecutables.map(i => ({
                    tipo: 'condicion',
                    etiqueta: this.etiquetaCondicion(i.original || i.dsl),
                    query: i.dsl,
                    accion: 'aplicar',
                    grupo: i.grupo || null
                }));
                mensaje.grupos = this.agruparPorTitulo(mensaje.sugerencias);
                mensaje.noSoportadas = items.filter(i => !(i.valido && i.dsl));
                if (pegado) {
                    // El original ya está en el mensaje del usuario: no hay texto que plegar
                    if (items.length === 0) mensaje.text = 'No encontré condiciones en ese texto.';
                } else {
                    // Sin nada traducido el texto es lo único que hay: se deja a la vista
                    mensaje.textoPlegado = items.length > 0;
                }
                mensaje.traduciendo = false;
                this.loading = false;
                this.scrollToBottom();
            },
            error: () => {
                if (pegado) {
                    // Sin licencia o con el modelo caído queda el flujo corto, que también traduce
                    this.mensajes = this.mensajes.filter(m => m !== mensaje);
                    this.enviarCorto(texto);
                    return;
                }
                mensaje.traduciendo = false;
                mensaje.errorTraduccion = true;
                this.loading = false;
            }
        });
    }

    // Consecutivas con el mismo título van juntas, en el orden del texto
    private agruparPorTitulo(sugerencias: any[]): GrupoChipsUI[] | undefined {
        const grupos: GrupoChipsUI[] = [];
        for (const s of sugerencias) {
            const ultimo = grupos[grupos.length - 1];
            if (ultimo && ultimo.titulo === s.grupo) {
                ultimo.sugerencias.push(s);
            } else {
                grupos.push({ titulo: s.grupo, sugerencias: [s] });
            }
        }
        // Con una sola categoría el subtítulo no aporta nada
        return grupos.filter(g => g.titulo).length > 1 ? grupos : undefined;
    }

    totalCondiciones(m: ChatMensajeUI): number {
        return (m.sugerencias?.length || 0) + (m.noSoportadas?.length || 0);
    }

    private etiquetaCondicion(original: string): string {
        const plana = original.replace(/\s+/g, ' ').trim();
        return plana.length > 60 ? plana.substring(0, 57) + '…' : plana;
    }

    accionarSugerencia(s: any): void {
        const accion = s.accion || 'aplicar'; // fallback si accion es null (cache viejo)
        if (accion === 'aplicar' && this.onSaveCallback) {
            // Modo guardar: marcar/desmarcar para guardar varias a la vez
            this.toggleSeleccion(s.query);
        } else if (accion === 'aplicar') {
            this.condicionSeleccionada = s.query;
            this.condicionaguardar = s.query;
            // Aplicar inmediatamente si hay callback (ej. contexto modelos)
            if (this.data.onCondicionChange) {
                this.data.onCondicionChange(s.query, this.direcciones.get(s.query));
            }
        } else if (accion === 'combinar') {
            // Combinar con la condición actual
            const base = this.condicionaguardar?.trim() || this.condicionSeleccionada?.trim();
            const combinada = base ? `${base} AND ${s.query}` : s.query;
            this.condicionSeleccionada = combinada;
            this.condicionaguardar = combinada;
            // Aplicar inmediatamente si hay callback (ej. contexto modelos)
            if (this.data.onCondicionChange) {
                this.data.onCondicionChange(combinada);
            }
        } else if (accion === 'refinar') {
            // Continuar explorando en el chat
            this.enviar(s.query);
        }
    }

    aplicarCondicionActual(): void {
        if (this.onSaveCallback) {
            // Seleccionadas + la combinada (si se construyó con "combinar")
            const condiciones = [...this.condicionesSeleccionadas];
            const combinada = this.condicionaguardar?.trim();
            if (combinada && !condiciones.includes(combinada)) condiciones.push(combinada);
            if (condiciones.length === 0) return;
            const direcciones = condiciones
                .map(c => this.direcciones.get(c))
                .filter((d): d is DireccionUI => !!d);
            this.onSaveCallback(condiciones, direcciones);
            // Mantener la ventana abierta — el guardado ocurre en el callback
            this.limpiarSeleccion();
        } else if (this.condicionaguardar) {
            this.dialogRef.close(this.condicionaguardar);
        }
    }

    // Condición construida con "combinar" que aún no está en la lista de marcadas
    get combinadaPendiente(): boolean {
        const combinada = this.condicionaguardar?.trim();
        return !!combinada && !this.isSeleccionada(combinada);
    }

    get totalAGuardar(): number {
        return this.condicionesSeleccionadas.length + (this.combinadaPendiente ? 1 : 0);
    }

    get mostrarBarraSeleccion(): boolean {
        return !!this.onSaveCallback && this.totalAGuardar > 0 && !this.escribiendo;
    }

    toggleLista(): void {
        this.listaAbierta = !this.listaAbierta;
    }

    quitarCombinada(): void {
        this.condicionaguardar = '';
        this.condicionSeleccionada = '';
        if (this.totalAGuardar === 0) this.listaAbierta = false;
    }

    limpiarSeleccion(): void {
        this.condicionesSeleccionadas = [];
        this.quitarCombinada();
        this.listaAbierta = false;
        this.escribiendo = false;
    }

    isSeleccionada(query: string): boolean {
        return this.condicionesSeleccionadas.includes(query);
    }

    toggleSeleccion(query: string): void {
        if (this.isSeleccionada(query)) {
            this.quitarSeleccion(query);
        } else {
            this.condicionesSeleccionadas = [...this.condicionesSeleccionadas, query];
            this.escribiendo = false;
        }
    }

    // Sugerencias "aplicar" de un mensaje (las que se pueden marcar para guardar)
    private sugerenciasAplicables(m: ChatMensajeUI): string[] {
        return (m.sugerencias || [])
            .filter(s => (s.accion || 'aplicar') === 'aplicar' && s.query)
            .map(s => s.query);
    }

    tieneVariasAplicables(m: ChatMensajeUI): boolean {
        return !!this.onSaveCallback && this.sugerenciasAplicables(m).length > 1;
    }

    todasSeleccionadas(m: ChatMensajeUI): boolean {
        const queries = this.sugerenciasAplicables(m);
        return queries.length > 0 && queries.every(q => this.isSeleccionada(q));
    }

    toggleTodas(m: ChatMensajeUI): void {
        const queries = this.sugerenciasAplicables(m);
        if (this.todasSeleccionadas(m)) {
            this.condicionesSeleccionadas = this.condicionesSeleccionadas.filter(q => !queries.includes(q));
        } else {
            const nuevas = queries.filter(q => !this.isSeleccionada(q));
            this.condicionesSeleccionadas = [...this.condicionesSeleccionadas, ...nuevas];
            this.escribiendo = false;
        }
    }

    quitarSeleccion(query: string): void {
        this.condicionesSeleccionadas = this.condicionesSeleccionadas.filter(q => q !== query);
        if (this.totalAGuardar === 0) this.listaAbierta = false;
    }

    private cancelarPeticion(): void {
        this.peticion?.unsubscribe();
        this.loading = false;
    }

    resetConversacion(): void {
        this.cancelarPeticion();
        const reiniciar = () => {
            this.mensajes = [];
            this.historialIdeas = [];
            this.condicionSeleccionada = '';
            this.mostrarBienvenida();
        };
        this.peticion = this._sseservice.apiDelete(this.urlBorrarChat, this.token).subscribe({
            next: reiniciar,
            error: reiniciar
        });
    }

    resetAndQuery(pregunta: string): void {
        this.cancelarPeticion();
        const reiniciar = () => {
            this.mensajes = [];
            this.historialIdeas = [];
            this.condicionSeleccionada = '';
            this.condicionaguardar = '';
            this.isMinimized = false;
            this.dialogRef.updateSize('33vw', '70vh');
            this.mostrarBienvenida();
            this.enviar(pregunta);
        };
        // Si el borrado falla, la consulta sigue: perderla sin aviso sería peor que algo de historial
        this.peticion = this._sseservice.apiDelete(this.urlBorrarChat, this.token).subscribe({
            next: reiniciar,
            error: reiniciar
        });
    }

    toggleMinimize(): void {
        this.isMinimized = !this.isMinimized;
        if (this.isMinimized) {
            this.dialogRef.updateSize('320px', 'auto');
        } else {
            this.dialogRef.updateSize('33vw', '70vh');
        }
    }

    cancelar(): void {
        this.dialogRef.close(null);
    }

    ngOnDestroy(): void {
        if (this.subtema) this.subtema.unsubscribe();
        this.peticion?.unsubscribe();
    }

    onKeydown(event: KeyboardEvent): void {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            this.enviar();
        }
    }

    private scrollToBottom(): void {
        setTimeout(() => {
            const chat = document.querySelector('.chat-messages');
            if (chat) chat.scrollTop = chat.scrollHeight;
        }, 50);
    }

    badgeClass(tipo: string): string {
        switch (tipo) {
            case 'condicion': return 'badge-condicion';
            case 'variacion': return 'badge-variacion';
            case 'complemento': return 'badge-complemento';
            case 'patron': return 'badge-patron';
            default: return 'badge-condicion';
        }
    }

    accionIcon(accion: string, query?: string): string {
        if ((accion || 'aplicar') === 'aplicar' && this.onSaveCallback && query !== undefined) {
            return this.isSeleccionada(query) ? 'check_box' : 'check_box_outline_blank';
        }
        switch (accion) {
            case 'aplicar': return 'check';
            case 'combinar': return 'add';
            case 'refinar': return 'arrow_forward';
            default: return 'check';
        }
    }
}
